<script>
export const pageName = 'map-assessor';
export const pageRoute = '/model-achievement-programme/assessor';
export const title = 'MAP Assessor'
export const requireMembership = true;
export const requireCompleteAccount = true;

import { getCurrentUserId } from '../../js/auth.js';
import { commands } from '../../js/lib/functions.js';
import { navigate } from '@trullock/page-manager'
import { showToast } from '../../js/utils/toast.js'
import UserView from '../../../functions/domain/users/userView.js';
import { hideLoading, showLoading } from '../../js/utils/loading.js';
import { listenToProjection } from '@trullock/firebase-client';
import { rsoLevelToLevel } from '../../../functions/domain/certification-utils.js'

export default {
	data() {
		return {
			currentUser: null,
			bio: null,
			reason: null,
			availability: null,
			ready: false
		}
	},

	methods: {
		rsoLevelToLevel,
		async boot(opts) {
			await listenToProjection([UserView, getCurrentUserId()], async user => {
				this.currentUser = user;
				this.ready = true;
			});	
		},
		show() {
			commands.warmUp();

			this.bio = null;
			this.reason = null;
			this.availability = null;
		},
		async apply() {
			
			showLoading();

			let result = await commands.users.map.requestAssessorInterview({
				bio: this.bio,
				reason: this.reason,
				availability: this.availability
			});
			
			if(!result.success)
			{
				showToast({ message: 'Error submitting application', style: 'bg-danger'})
				hideLoading();
				return;
			}

			showToast({
				message: 'Application submitted',
				style: 'bg-success'
			})

			hideLoading();
		}
	}
}

</script>
<template>
	<div v-if="ready" class="container py-5">
		<div class="row justify-content-center">
			<div class="col-12 col-lg-8">
				<h1 class="display-4 text-center mb-3">MAP Assessor Application</h1>
				<p class="text-muted text-center mb-2">To become a UKRA MAP Assessor you must successfully complete an interview and pass a DBS Check.</p>
				<p class="text-muted text-center mb-5">Detailed information about the <a href="https://ukra.org.uk/certification/model-achievement-programme/" target="_blank">MAP Assessor Process <span class="fe fe-external-link"></span></a> can be found on the main UKRA website.</p>
			</div>
		</div>
		<div class="row">
			<div class="col col-12 col-lg-8">
				<div class="card" v-if="currentUser.map.assessorStatus == 'none'">
					<div class="card-header">
						<h4 class="card-header-title">Application</h4>
					</div>
					<div class="card-body">
						<form ref="form" @submit.prevent="apply">
							
							<div class="form-group">
								<label class="form-label mb-1">Biography</label>
								<textarea class="form-control" v-model="bio" required rows="4"></textarea>
								<span class="invalid-feedback"></span>
								<p class="form-text text-muted small mb-0">Tell us about yourself and your rocketry experience.</p>
							</div>
							
							<div class="form-group">
								<label class="form-label mb-1">Reasoning</label>
								<textarea class="form-control" v-model="reason" required rows="4"></textarea>
								<span class="invalid-feedback"></span>
								<p class="form-text text-muted small mb-0">Explain why you want to become, and why you would make a good MAP Assessor.</p>
							</div>

							<div class="form-group">
								<label class="form-label mb-1">Availability</label>
								<textarea class="form-control" v-model="availability" required rows="4"></textarea>
								<span class="invalid-feedback"></span>
								<p class="form-text text-muted small mb-0">Provide as many dates and times as possible that you can be available for a video call, and list any upcoming UKRA affiliated club meets youre likely to attend.</p>
							</div>

							<p>Please note: UKRA is staffed by volunteers on a part time basis and will respond to your enquiry as soon as possible.</p>
							<button type="submit" class="btn btn-lg btn-primary btn-block">Apply <span class="fe fe-check-circle"></span></button>
						</form>
					</div>
				</div>
				<div class="card" v-else-if="currentUser.map.assessorStatus == 'applied'">
					<div class="card-header">
						<h4 class="card-header-title">Interview</h4>
					</div>
					<div class="card-body">
						<p>We require you to conduct a ~30 minute interview with a UKRA official to determine your suitability for the role of a MAP Assessor.</p>
						<p>We will be in touch as soon as possible to arrange a date for your interview.</p>
						<p class="mb-0">To prepare, please familiarise yourself with the <a href="/model-achievement-programme">MAP Tasks</a> and the <a href="https://ukra.org.uk/certification/model-achievement-programme/">MAP Assessor Rules and Guidance</a>, as well as the <a href="https://ukra.org.uk/safety/safeguarding/" target="_blank">Safeguarding policies</a>.</p>
					</div>
				</div>
				<div class="card" v-else-if="currentUser.map.assessorStatus == 'approved'">
					<div class="card-header">
						<h4 class="card-header-title">DBS Check</h4>
					</div>
					<div class="card-body">
						<p>We require you to pass a DBS check.</p>
						<a href="/account/dbs" class="btn btn-primary">Complete DBS Check <span class="fe fe-arrow-right-circle"></span></a>
					</div>
				</div>
				<div class="card" v-else-if="currentUser.map.assessorStatus == 'passed'">
					<div class="card-header">
						<h4 class="card-header-title">You are a MAP Assessor</h4>
					</div>
					<div class="card-body">
						<p class="mb-0">Congratulations! You are a certified MAP Assessor.</p>
					</div>
				</div>
			</div>
			<div class="col col-12 col-lg-4">
				<div class="card">
					<div class="card-header">
						<h4 class="card-header-title">Progress</h4>
					</div>
					<div class="card-body">
						<div class="list-group list-group-flush list-group-activity my-n3">
							<div class="list-group-item">
								<div class="row">
									<div class="col-auto">
										<div class="avatar avatar-sm">
											<div :class="`avatar-title fs-lg rounded-circle ${currentUser.map.assessorStatus == 'none' ? 'text-white bg-primary' : 'text-white bg-success'}`">
												<i class="fe fe-play"></i>
											</div>
										</div>
									</div>
									<div class="col mb-n2">
										<h5 class="mb-1">Start application</h5>
										<p class="small text-gray-700 mb-0">Apply to be a MAP Assessor</p>
										<p class="text-body-secondary mb-0">5 mins, online</p>
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row">
									<div class="col-auto">
										<div class="avatar avatar-sm">
											<div :class="`avatar-title fs-lg rounded-circle ${currentUser.map.assessorStatus == 'applied' ? 'text-white bg-primary' : currentUser.map.assessorStatus == 'none' ? 'text-secondary bg-light' : 'text-white bg-success'}`">
												<i class="fe fe-users"></i>
											</div>
										</div>
									</div>
									<div class="col mb-n2">
										<h5 class="mb-1">Interview</h5>
										<p class="small text-gray-700 mb-0">Prove yourself capable</p>
										<p class="text-body-secondary mb-0">30 mins, online or in-person</p>
									</div>
								</div>
							</div>
							
							<div class="list-group-item">
								<div class="row">
									<div class="col-auto">
										<div class="avatar avatar-sm">
											<div :class="`avatar-title fs-lg rounded-circle ${currentUser.map.assessorStatus == 'approved' ? 'text-white bg-primary' : currentUser.map.assessorStatus == 'passed' ? 'text-white bg-success' : 'text-secondary bg-light'}`">
												<i class="fe fe-check-circle"></i>
											</div>
										</div>
									</div>
									<div class="col mb-n2">
										<h5 class="mb-1">DBS Check</h5>
										<p class="small text-gray-700 mb-0">Pass safety check</p>
										<p class="text-body-secondary mb-0">1-2 weeks</p>
									</div>
								</div>
							</div>
							
							<div class="list-group-item">
								<div class="row">
									<div class="col-auto">
										<div class="avatar avatar-sm">
											<div :class="`avatar-title fs-lg rounded-circle ${currentUser.map.assessorStatus == 'passed' ? 'text-white bg-success' : 'text-secondary bg-light'}`">
												<i class="fe fe-check-square"></i>
											</div>
										</div>
									</div>
									<div class="col mb-n2">
										<h5 class="mb-1">Certified</h5>
										<p class="small text-gray-700 mb-0">Become a MAP Assessor</p>
										<p class="text-body-secondary mb-0">Instant</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
    </div>
</template>
